import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };
const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const emptyForm = { title: '', message: '', is_pinned: false, image_url: '' };

const AnnouncementModal = ({ title, formData, setFormData, imagePreview, onImageChange, onRemoveImage, onSubmit, onCancel, uploading }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" style={card}>
      <h2 className="text-[16px] font-semibold text-[#3b3b3b] mb-5">{title}</h2>
      <div className="space-y-4">
        <div>
          <label className={LABEL}>Title *</label>
          <input type="text" value={formData.title}
            onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
            className={INPUT} placeholder="Masukkan title announcement" />
        </div>
        <div>
          <label className={LABEL}>Message *</label>
          <textarea value={formData.message} rows={5}
            onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
            className={INPUT + ' resize-none'} placeholder="Masukkan pesan announcement" />
        </div>
        <div>
          <label className={LABEL}>Foto (opsional)</label>
          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={onImageChange}
            className="w-full text-[13px] text-[#646464] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[12px] file:font-medium file:bg-[#ecf8fb] file:text-[#2a798c] hover:file:bg-[#3fb5d2]/10 cursor-pointer" />
          {imagePreview && (
            <div className="mt-2 relative inline-block">
              <img src={imagePreview} alt="Preview" className="max-h-40 rounded-xl border border-[#e5e5e5] object-cover" />
              <button type="button" onClick={onRemoveImage}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#c62828] text-white rounded-full text-[11px] flex items-center justify-center hover:bg-red-700">
                ×
              </button>
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={formData.is_pinned}
            onChange={(e) => setFormData(p => ({ ...p, is_pinned: e.target.checked }))}
            className="h-4 w-4 rounded border-[#e5e5e5] text-[#3fb5d2] focus:ring-[#3fb5d2]" />
          <span className="text-[13px] text-[#646464]">Pin announcement</span>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onCancel}
            className="px-4 py-2 text-[13px] font-medium text-[#646464] border border-[#e5e5e5] rounded-lg hover:bg-gray-50 transition-colors">
            Batal
          </button>
          <button onClick={onSubmit} disabled={uploading}
            className="px-4 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors disabled:opacity-50">
            {uploading ? 'Mengupload...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => { loadAnnouncements(); }, []);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/announcements');
      setAnnouncements(Array.isArray(response) ? response : []);
    } catch (error) {
      alert('Error loading announcements: ' + (error.response?.data?.error || error.message));
      setAnnouncements([]);
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview('');
  };

  const handleEdit = (a) => {
    setEditingAnnouncement(a);
    setFormData({ title: a.title || '', message: a.message || '', is_pinned: a.is_pinned || false, image_url: a.image_url || '' });
    setImageFile(null);
    setImagePreview(a.image_url || '');
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('File harus berupa gambar'); return; }
    if (file.size > 5 * 1024 * 1024)    { alert('Ukuran file maksimal 5MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(p => ({ ...p, image_url: '' }));
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return formData.image_url || null;
    setUploadingImage(true);
    const base64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(imageFile);
    });
    const { image_url } = await api.post('/api/announcements/upload-image', { image_base64: base64, filename: imageFile.name });
    setUploadingImage(false);
    return image_url;
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.message) { alert('Title dan message harus diisi'); return; }
    try {
      const imageUrl = await uploadImageIfNeeded();
      await api.post('/api/announcements', { title: formData.title, message: formData.message, is_pinned: formData.is_pinned, ...(imageUrl && { image_url: imageUrl }) });
      await loadAnnouncements();
      setShowCreateModal(false);
      resetForm();
      alert('Announcement berhasil dibuat');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdate = async () => {
    if (!formData.title || !formData.message) { alert('Title dan message harus diisi'); return; }
    try {
      const imageUrl = await uploadImageIfNeeded();
      await api.put(`/api/announcements/${editingAnnouncement.id}`, { title: formData.title, message: formData.message, is_pinned: formData.is_pinned, image_url: imageUrl });
      await loadAnnouncements();
      setEditingAnnouncement(null);
      resetForm();
      alert('Announcement berhasil diupdate');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus announcement ini?')) return;
    try {
      await api.delete(`/api/announcements/${id}`);
      await loadAnnouncements();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Announcements</h1>
        <button onClick={() => { setShowCreateModal(true); resetForm(); }}
          className="flex items-center justify-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors w-full sm:w-auto">
          + Buat Announcement
        </button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <AnnouncementModal
          title="Buat Announcement Baru"
          formData={formData} setFormData={setFormData}
          imagePreview={imagePreview}
          onImageChange={handleImageChange}
          onRemoveImage={handleRemoveImage}
          onSubmit={handleCreate}
          onCancel={() => { setShowCreateModal(false); resetForm(); }}
          uploading={uploadingImage}
        />
      )}

      {/* Edit Modal */}
      {editingAnnouncement && (
        <AnnouncementModal
          title="Edit Announcement"
          formData={formData} setFormData={setFormData}
          imagePreview={imagePreview}
          onImageChange={handleImageChange}
          onRemoveImage={handleRemoveImage}
          onSubmit={handleUpdate}
          onCancel={() => { setEditingAnnouncement(null); resetForm(); }}
          uploading={uploadingImage}
        />
      )}

      {announcements.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" style={card}>
          <p className="text-[14px] text-[#8a8a8a]">Tidak ada announcement.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((a) => (
            <div key={a.id} className={`bg-white rounded-2xl p-5 ${a.is_pinned ? 'border-l-4 border-amber-400' : ''}`} style={card}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <h3 className="text-[15px] font-semibold text-[#3b3b3b]">{a.title}</h3>
                    {a.is_pinned && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        📌 PINNED
                      </span>
                    )}
                  </div>
                  {a.image_url && (
                    <img src={a.image_url} alt="Announcement" className="max-h-48 rounded-xl border border-[#e5e5e5] object-cover mb-3" />
                  )}
                  <p className="text-[13px] text-[#646464] leading-5 whitespace-pre-wrap mb-2">{a.message}</p>
                  <p className="text-[11px] text-[#8a8a8a]">{fmtDate(a.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleEdit(a)}
                    className="text-[12px] font-medium px-3 py-1.5 rounded-lg bg-[#ecf8fb] text-[#2a798c] hover:bg-[#3fb5d2]/20 transition-colors">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(a.id)}
                    className="text-[12px] font-medium px-3 py-1.5 rounded-lg bg-red-50 text-[#c62828] hover:bg-red-100 transition-colors">
                    Hapus
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Announcements;
